import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ISimpleTextareaResizableProps, SimpleTextareaResizable } from "./SimpleTextareaResizable";

interface ISimpleTextareaResizableWithAutocompleteProps extends ISimpleTextareaResizableProps {
    autoCompleteItems: SearchableItemModelT<string>[];
    onAutoCompleteItemSelected: (selectedText: string) => void;
}

class SimpleTextareaResizableWithAutocompleteState {
    public currentlySelectedAutocompleteItemIndex?: number;
    public shouldShowAutoComplete: boolean;
}

class SimpleTextareaResizableWithAutocomplete extends BaseReactComponent<ISimpleTextareaResizableWithAutocompleteProps, SimpleTextareaResizableWithAutocompleteState> {
    private autocompleteMenuItemsRoleId: string;

    constructor(props: ISimpleTextareaResizableWithAutocompleteProps) {
        super(props);

        this.state = {
            currentlySelectedAutocompleteItemIndex: null,
            shouldShowAutoComplete: false
        };

        this.autocompleteMenuItemsRoleId = `autocomplete-menu-${sessionStore.newId()}-link`;
    }

    public render(): JSX.Element {
        return (
            <div>
                <SimpleTextareaResizable {...this.getSimpleTextareaProps()} ref={null} />
                {this.renderAutoCompleteList()}
            </div>
        );
    }

    public renderAutoCompleteList(): JSX.Element {
        const autoCompleteList = this.getAutoCompleteList();

        if (!this.state.shouldShowAutoComplete || !autoCompleteList.any())
            return null;

        return (
            <div
                role="autocomplete-menu"
                style={{ position: "absolute", backgroundColor: "white", border: "solid 1px #ccc", padding: "4px", zIndex: 1000 }}
            >
                <ul className="results-list">
                    {autoCompleteList.map((item: SearchableItemModelT<string>, index: number) => (
                        <li key={item.Value}>
                            <div
                                className={"flex-container " + (index === this.state.currentlySelectedAutocompleteItemIndex ? "active" : "")}
                                style={index === this.state.currentlySelectedAutocompleteItemIndex ? { "backgroundColor": "#f4f6f9" } : {}}
                                onClick={() => this.onAutoCompleteItemSelected(item.Value)}
                            >
                                <a role={this.autocompleteMenuItemsRoleId} tabIndex={1}>{item.Value}</a>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    private getSimpleTextareaProps(): ISimpleTextareaResizableProps {
        // get valid textarea props
        let textareaProps = { ...this.props };
        delete textareaProps.onAutoCompleteItemSelected;
        delete textareaProps.autoCompleteItems;

        // hook events
        if (this.props.autoCompleteItems?.any()) {
            textareaProps.onFocus = event => this.onTextareaFocus(event);
            textareaProps.onKeyDown = event => this.onTextareaKeyDown(event);
            textareaProps.onBlur = event => this.onTextareaBlur(event);
        }

        return textareaProps;
    }

    private getAutoCompleteList(): SearchableItemModelT<string>[] {
        if (!this.props.autoCompleteItems?.any() || Array.isArray(this.props.value))
            return [];

        const normalizedSearchText = cacheStore.Helper.RemoveDiacritics(this.props.value as string)?.toLowerCase() ?? "";
        return this.props.autoCompleteItems?.filter(item => item.Value_Searchable.contains(normalizedSearchText) && item.Value_Searchable !== normalizedSearchText) ?? [];
    }

    private onAutoCompleteItemSelected(selectedText: string): void {
        this.mutateState(
            s => {
                s.shouldShowAutoComplete = false;
                s.currentlySelectedAutocompleteItemIndex = null;
            },
            () => this.props.onAutoCompleteItemSelected(selectedText)
        );
    }

    private onTextareaBlur(event: React.FocusEvent<HTMLTextAreaElement>): void {
        event.persist();

        if ((event.relatedTarget as Element)?.getAttribute("role") !== this.autocompleteMenuItemsRoleId) {
            this.mutateState(
                s => {
                    s.shouldShowAutoComplete = false;
                    s.currentlySelectedAutocompleteItemIndex = null;
                },
                () => this.props.onBlur?.(event)
            );
        }

        else
            this.props.onBlur?.(event);
    }

    private onTextareaFocus(event: React.FocusEvent<HTMLTextAreaElement>): void {
        this.mutateState(
            s => s.shouldShowAutoComplete = true,
            () => this.props.onFocus?.(event)
        );
    }

    private onTextareaKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
        event.persist();

        const isAutoCompleteControlKeyPressed: boolean = Object.keys(AutoCompleteListKeyboardControlKeys).contains(event.key);

        if (this.state.shouldShowAutoComplete && this.getAutoCompleteList().any() && isAutoCompleteControlKeyPressed) {
            switch (event.key) {
                case AutoCompleteListKeyboardControlKeys.ArrowUp:
                    event.preventDefault();
                    this.incrementSelectedAutocompleteItemIndex(-1);
                    return;

                case AutoCompleteListKeyboardControlKeys.ArrowDown:
                    event.preventDefault();
                    this.incrementSelectedAutocompleteItemIndex(1);
                    return;

                case AutoCompleteListKeyboardControlKeys.Enter:
                    if (this.state.currentlySelectedAutocompleteItemIndex != null) {
                        event.preventDefault();
                        this.onAutoCompleteItemSelected(this.getAutoCompleteList()[this.state.currentlySelectedAutocompleteItemIndex].Value);
                    }

                    return;

                case AutoCompleteListKeyboardControlKeys.Escape:
                    event.preventDefault();
                    this.mutateState(s => s.shouldShowAutoComplete = false);
                    return;

                default:
                    this.props.onKeyDown?.(event);
            }
        }

        this.mutateState(
            s => {
                s.shouldShowAutoComplete = true;
                s.currentlySelectedAutocompleteItemIndex = null;
            },
            () => this.props.onKeyDown?.(event)
        );
    }

    private incrementSelectedAutocompleteItemIndex(increment: number): void {
        const nextIndex = (this.state.currentlySelectedAutocompleteItemIndex ?? -1) + increment;
        const autoCompleteList = this.getAutoCompleteList();
        const autoCompleteListLastIndex = autoCompleteList.length - 1;

        const normalizedNextIndex = Math.min(Math.max(nextIndex, 0), autoCompleteListLastIndex);

        this.mutateState(s => s.currentlySelectedAutocompleteItemIndex = (normalizedNextIndex >= 0 ? normalizedNextIndex : null));
    }
}

enum AutoCompleteListKeyboardControlKeys {
    ArrowUp = "ArrowUp",
    ArrowDown = "ArrowDown",
    Enter = "Enter",
    Escape = "Escape"
}

export { SimpleTextareaResizableWithAutocomplete };
